/**
 * Subscribes to a thread's message WebSocket to render streamed assistant text
 * and detect end-of-turn (an assistant message with non-empty text content,
 * which under stopOnResponse means the agent has finished its turn).
 */
import type { ApiClient } from "./api.ts";
import { Heartbeat } from "./heartbeat.ts";

export interface StreamHooks {
  /** A streamed fragment of the assistant's visible answer. `messageId` lets the
   *  consumer reset its live buffer when a new message starts. */
  onChunk(text: string, messageId?: string): void;
  /**
   * A streamed fragment of the model's internal reasoning. This is the opt-in
   * `reasoning_chunk` channel (we subscribe with `?reasoning=1`); the backend
   * only sends it to sockets that ask. See the spec `ThreadReasoningChunkEvent`.
   */
  onReasoningChunk?(text: string, messageId?: string): void;
  /**
   * A complete assistant message. `hasToolCalls` is true for narration that
   * precedes tool calls (the turn continues) and false for the final answer
   * (the turn is over).
   */
  onAssistantText(content: string, hasToolCalls: boolean): void;
  /**
   * A custom thread event (the `{ type: "event", eventType, data }` channel) —
   * e.g. `tool_call_started`, `tool_call_done`, `generation`.
   */
  onEvent?(eventType: string, data: any): void;
  onError(err: string): void;
}

export class MessageStream {
  private ws: WebSocket | null = null;
  private closed = false;
  private heartbeat: Heartbeat | null = null;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private resolveConnected: (() => void) | null = null;

  constructor(
    private api: ApiClient,
    private threadId: string,
    private hooks: StreamHooks
  ) {}

  /**
   * Connect and stay connected. Resolves on first open; reconnects on any drop
   * with exponential backoff. Silent — the bridge surfaces the user-facing
   * connection status; completion is detected via HTTP polling regardless, so a
   * dropped stream only affects live display, not correctness.
   */
  connect(): Promise<void> {
    return new Promise((resolve) => {
      let settled = false;
      this.resolveConnected = () => {
        if (!settled) {
          settled = true;
          resolve();
        }
      };
      setTimeout(() => this.resolveConnected?.(), 8000);
      this.openSocket();
    });
  }

  private openSocket(): void {
    if (this.closed) return;
    // `reasoning=1` opts into the internal-reasoning channel (off by default).
    const url = `${this.api.wsEndpoint}/api/threads/${this.threadId}/stream?token=${encodeURIComponent(this.api.bearer)}&reasoning=1`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.addEventListener("open", () => {
      this.reconnectAttempt = 0;
      this.startHeartbeat(ws);
      this.resolveConnected?.();
    });
    ws.addEventListener("message", (ev) => {
      // Any frame proves the link is alive; a silent stream would otherwise
      // freeze the live indicators without ever reconnecting.
      if (this.ws === ws) this.heartbeat?.markAlive();
      this.onMessage(String((ev as MessageEvent).data));
    });
    // Both 'error' (failed open) and 'close' must drive reconnection.
    ws.addEventListener("error", () => this.handleDrop(ws));
    ws.addEventListener("close", () => this.handleDrop(ws));
  }

  private handleDrop(ws: WebSocket): void {
    if (this.ws !== ws) return;
    this.ws = null;
    this.stopHeartbeat();
    this.scheduleReconnect();
  }

  private startHeartbeat(ws: WebSocket): void {
    this.stopHeartbeat();
    this.heartbeat = new Heartbeat(ws, () => this.handleDrop(ws));
    this.heartbeat.start();
  }

  private stopHeartbeat(): void {
    if (this.heartbeat) {
      this.heartbeat.stop();
      this.heartbeat = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.closed || this.reconnectTimer) return;
    this.reconnectAttempt++;
    const base = Math.min(500 * 2 ** (this.reconnectAttempt - 1), 15000);
    const delay = base + Math.floor(Math.random() * 400);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
  }

  close(): void {
    this.closed = true;
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
  }

  private onMessage(raw: string): void {
    let msg: any;
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }
    if (msg.type === "event" && typeof msg.eventType === "string") {
      this.hooks.onEvent?.(msg.eventType, msg.data);
      return;
    }
    if (msg.type === "message_chunk" && (msg.depth ?? 0) === 0) {
      if (typeof msg.chunk === "string") this.hooks.onChunk(msg.chunk, msg.message_id);
      return;
    }
    if (msg.type === "reasoning_chunk" && (msg.depth ?? 0) === 0) {
      if (typeof msg.chunk === "string") this.hooks.onReasoningChunk?.(msg.chunk, msg.message_id);
      return;
    }
    if (msg.type === "message_data" && (msg.depth ?? 0) === 0) {
      const data = msg.data || {};
      if (data.role === "assistant" && typeof data.content === "string" && data.content.trim()) {
        // tool_calls is serialized as a JSON string, not an array.
        const tc = data.tool_calls;
        const hasToolCalls = Array.isArray(tc)
          ? tc.length > 0
          : typeof tc === "string" && tc.trim() !== "" && tc.trim() !== "null" && tc.trim() !== "[]";
        this.hooks.onAssistantText(data.content, hasToolCalls);
      }
      if (data.role === "assistant" && data.status === "failed" && data.error) {
        this.hooks.onError(String(data.error));
      }
    }
  }
}
